/**
 * <s-line-total> — Shows the price for the chosen quantity beside the product form's
 * quantity selector, so shoppers see the total update as they add more.
 *
 * Attributes:
 *   data-unit-price — Selected variant price in minor units (pence), from Liquid.
 *   data-currency   — ISO currency code of the cart, for Intl formatting.
 *   data-locale     — Storefront locale, for Intl formatting.
 *   data-input      — ID of the quantity input to watch.
 *
 * Children:
 *   [data-label-one] / [data-label-many] — Label shown for one item / for more than one.
 *   [data-total] — Receives the formatted total.
 *   [data-each]  — Server-rendered "£x each" line, shown only when quantity > 1.
 *
 * The product-form partial re-renders this element on variant change, so the unit
 * price is always the selected variant's.
 */
class SLineTotal extends HTMLElement {
  #ac;
  #format;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    try {
      this.#format = new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'GBP',
      });
    } catch {
      this.#format = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' });
    }

    const form = this.closest('form') ?? document;
    // Quantity changes arrive as the selector's own event (+/- buttons) or native input events (typing).
    form.addEventListener('s-quantity:change', () => this.#render(), opts);
    form.addEventListener('input', (e) => {
      if (e.target instanceof HTMLInputElement && e.target.id === this.dataset.input) this.#render();
    }, opts);
    form.addEventListener('change', (e) => {
      if (e.target instanceof HTMLInputElement && e.target.id === this.dataset.input) this.#render();
    }, opts);

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #render() {
    const input = document.getElementById(this.dataset.input);
    const unit = Number(this.dataset.unitPrice);
    if (!(input instanceof HTMLInputElement) || !Number.isFinite(unit)) return;
    const quantity = Math.max(1, Math.floor(Number(input.value) || 1));
    const many = quantity > 1;

    const total = this.querySelector('[data-total]');
    if (total) total.textContent = this.#format.format((unit * quantity) / 100);
    this.querySelector('[data-label-one]')?.toggleAttribute('hidden', many);
    this.querySelector('[data-label-many]')?.toggleAttribute('hidden', !many);
    this.querySelector('[data-each]')?.toggleAttribute('hidden', !many);
  }
}

if (!customElements.get('s-line-total')) customElements.define('s-line-total', SLineTotal);
